import { formatPropLockTime } from '@/utils/propBasedDailyReveal';

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  timeZone: 'America/New_York',
  weekday: 'short',
  month: 'short',
  day: 'numeric',
};

/**
 * "Today - Mon, Oct 5" only when the reveal really is today (ET); otherwise the reveal's own
 * date. It used to print the device's date unconditionally, so a reveal opening tomorrow read
 * as today's (DKPLAT-882).
 */
function formatRevealDate(revealOpenAt: string | null | undefined): string {
  const today = new Date().toLocaleDateString('en-US', DATE_FORMAT_OPTIONS);
  const revealDate = revealOpenAt ? new Date(revealOpenAt) : null;
  if (!revealDate || Number.isNaN(revealDate.getTime())) return `Today - ${today}`;
  const revealDay = revealDate.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS);
  return revealDay === today ? `Today - ${today}` : revealDay;
}

type RevealChestSectionProps = {
  onRevealPlayers?: () => void;
  dayStatus?: 'upcoming' | 'open' | 'locked' | 'graded' | null;
  alreadyRevealed?: boolean;
  /** NFL prop-based: today's category (e.g. Ground King). */
  categoryName?: string | null;
  /** Day-level pick cutoff or prop lock deadline. */
  pickCutoffAt?: string | null;
  /** When the day's reveal opens — decides whether the date reads as "Today". */
  revealOpenAt?: string | null;
  isPropBased?: boolean;
};

export default function RevealChestSection({
  onRevealPlayers,
  dayStatus,
  alreadyRevealed,
  categoryName,
  pickCutoffAt,
  revealOpenAt,
  isPropBased = false,
}: RevealChestSectionProps) {
  const revealDate = formatRevealDate(revealOpenAt);
  const displayDate = categoryName ? `${categoryName} · ${revealDate}` : revealDate;
  const lockLabel = isPropBased && pickCutoffAt ? formatPropLockTime(pickCutoffAt) : null;
  const canReveal = dayStatus === 'open' || dayStatus === null || dayStatus === undefined;
  const buttonLabel = alreadyRevealed
    ? isPropBased
      ? 'View Props'
      : 'View Players'
    : isPropBased
      ? 'Reveal Props'
      : 'Reveal Players';

  return (
    <section className="bg-[#121212] pb-5 pt-4">
      <h2 className="daily-reveal-heading mb-1 text-center text-[14px] uppercase tracking-[0.42px]">
        {isPropBased ? "Reveal Today's Props" : "Reveal Today's Chest"}
      </h2>
      <p
        className="mb-1 text-center text-[#8A8A8A]"
        style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: '12px',
        }}
      >
        {displayDate}
      </p>
      {lockLabel && (
        <p
          className="mb-3 text-center text-[#BCBCBC]"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: '11px',
          }}
        >
          Lock {lockLabel}
        </p>
      )}
      {!lockLabel && <div className="mb-3" />}

      <div
        className="relative mx-auto h-[110px] w-full overflow-visible rounded-[10px]"
        style={{
          background: 'linear-gradient(180deg, #242424 0%, #121212 100%)',
          border: '1px solid #6F6F6F',
        }}
      >
        <div className="relative z-10 flex h-full items-center px-4">
          <button
            type="button"
            aria-label={buttonLabel}
            onClick={onRevealPlayers}
            disabled={!canReveal}
            className="daily-reveal-cta-button relative h-[42px] w-[157px] shrink-0 disabled:opacity-50"
          >
            <img
              src="/shapes/Reveal-player-cta.svg"
              alt=""
              aria-hidden
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            />
            <span className="daily-reveal-button-primary relative z-10 inline-flex h-full w-full items-center justify-center uppercase">
              {buttonLabel}
            </span>
          </button>

          <div className="pointer-events-none absolute -bottom-6 -right-4 z-20">
            <img
              src="/DK-box.svg"
              alt="Daily reveal chest"
              className="h-[184px] w-[184px] shrink-0 object-cover object-center"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
